import type { PdlAffordabilityJourneyDto } from '../api/admin';

export type PdlRemarkTone = 'info' | 'success' | 'warning' | 'error' | 'neutral';

export type PdlRemarkFactor = {
  label: string;
  value: string;
};

export type PdlRemarkCard = {
  code: string;
  category: string;
  categoryLabel: string;
  title: string;
  tone: PdlRemarkTone;
  factors: PdlRemarkFactor[];
};

export type PdlSummaryFactor = {
  label: string;
  value: string;
  tone?: PdlRemarkTone;
};

const CATEGORY_LABELS: Record<string, string> = {
  REGULATORY: 'Regulatory',
  AFFORDABILITY: 'Cost of living',
  CALC: 'Capacity math',
  SIGNAL: 'Risk signal',
  ELOD: 'Bank data',
};

const CATEGORY_ORDER = ['REGULATORY', 'AFFORDABILITY', 'CALC', 'SIGNAL', 'ELOD'];

function fmtMoney(n: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  }).format(n);
}

function fmtPct(ratio: number): string {
  const pct = ratio <= 1 ? ratio * 100 : ratio;
  return `${pct.toFixed(pct % 1 === 0 ? 0 : 1)}%`;
}

function readNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  return null;
}

function readString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  return null;
}

function parseMoneyFromMessage(message: string, pattern: RegExp): string | null {
  const match = message.match(pattern);
  if (!match?.[1]) return null;
  const raw = match[1].replace(/,/g, '');
  const n = Number(raw);
  return Number.isFinite(n) ? fmtMoney(n) : `$${match[1]}`;
}

export function parsePdlSummaryFactors(summary?: string | null): PdlSummaryFactor[] {
  if (!summary?.trim()) return [];

  const factors: PdlSummaryFactor[] = [];
  const approved = summary.match(/Approved\s+(\$[\d,]+)/i);
  const declined = summary.match(/Declined/i);
  if (approved) {
    factors.push({ label: 'Outcome', value: `Approved ${approved[1]}`, tone: 'success' });
  } else if (declined) {
    factors.push({ label: 'Outcome', value: 'Declined', tone: 'error' });
  }

  const income = summary.match(/Income\s+(\$[\d,]+(?:\/mo)?)/i);
  if (income) factors.push({ label: 'Income', value: income[1].replace(/\/mo/i, '') + '/mo' });

  const debt = summary.match(/existing debt\s+(\$[\d,]+(?:\/mo)?)/i);
  if (debt) factors.push({ label: 'Existing debt', value: debt[1].replace(/\/mo/i, '') + '/mo' });

  const room = summary.match(/room\s+(\$[\d,]+(?:\/mo)?)/i);
  if (room) factors.push({ label: 'Payment room', value: room[1].replace(/\/mo/i, '') + '/mo' });

  return factors;
}

export function presentPdlRemark(remark: {
  code: string;
  category: string;
  message: string;
  value?: number | string | boolean | null;
}): PdlRemarkCard {
  const { code, category, message, value } = remark;
  const categoryLabel = CATEGORY_LABELS[category] ?? category;

  switch (code) {
    case 'FEDERAL_COST_CAP':
      return {
        code,
        category,
        categoryLabel,
        title: 'Federal cost cap',
        tone: 'info',
        factors: [
          { label: 'Max cost', value: '$14 per $100' },
          { label: 'Typical APR', value: '~365% (14-day term)' },
        ],
      };

    case 'FEDERAL_PRODUCT_LIMITS':
      return {
        code,
        category,
        categoryLabel,
        title: 'Federal product limits',
        tone: 'info',
        factors: [
          { label: 'Max principal', value: fmtMoney(readNumber(value) ?? 1500) },
          { label: 'Max term', value: '62 days' },
        ],
      };

    case 'PROVINCIAL_NET_PAY_CAP': {
      const ratio = readNumber(value);
      return {
        code,
        category,
        categoryLabel,
        title: 'Provincial net-pay cap',
        tone: 'info',
        factors: [
          {
            label: 'Statutory limit',
            value: ratio != null ? `${fmtPct(ratio)} of net pay` : 'See province rule',
          },
          { label: 'Effect', value: 'Informational — internal PTI may be stricter' },
        ],
      };
    }

    case 'MBM_RESIDUAL_INCOME': {
      const status = readString(value) ?? '—';
      const essentials = parseMoneyFromMessage(message, /essential floor ~(\$[\d,]+)/i);
      const residual = parseMoneyFromMessage(message, /residual ~(\$[\d,]+)/i);
      const province = message.match(/\b(ON|BC|AB|QC|MB|SK|NS|NB|NL|PE)\b/)?.[1];
      return {
        code,
        category,
        categoryLabel,
        title: 'MBM residual check',
        tone: status === 'STRESS' ? 'warning' : 'success',
        factors: [
          { label: 'Status', value: status === 'STRESS' ? 'Under pressure' : 'OK' },
          ...(province ? [{ label: 'Region', value: province }] : []),
          ...(essentials ? [{ label: 'Essential floor', value: `${essentials}/mo` }] : []),
          ...(residual ? [{ label: 'Residual after offer', value: `${residual}/mo` }] : []),
          { label: 'Decision impact', value: 'None — note only' },
        ],
      };
    }

    case 'PTI_TDS_CALC': {
      const roomCents = readNumber(value);
      const room = roomCents != null ? fmtMoney(roomCents / 100) : null;
      return {
        code,
        category,
        categoryLabel,
        title: 'Payment capacity',
        tone: 'neutral',
        factors: [
          ...(room ? [{ label: 'Room available', value: `${room}/mo` }] : []),
          { label: 'Rule', value: 'min(40% PTI, 50% TDS − debt)' },
        ],
      };
    }

    case 'PAYDAY_STACKING':
      return {
        code,
        category,
        categoryLabel,
        title: 'Payday lender stacking',
        tone: 'warning',
        factors: [
          { label: 'Lenders on account', value: String(readNumber(value) ?? '—') },
          { label: 'Decision impact', value: 'None — note only' },
        ],
      };

    case 'DEBT_NSF':
      return {
        code,
        category,
        categoryLabel,
        title: 'Returned loan payments',
        tone: 'warning',
        factors: [
          { label: 'NSF count (90d)', value: String(readNumber(value) ?? '—') },
          { label: 'Decision impact', value: 'None — note only' },
        ],
      };

    case 'DEBT_SERVICE_RATIO':
      return {
        code,
        category,
        categoryLabel,
        title: 'Debt service ratio',
        tone: 'warning',
        factors: [
          {
            label: 'Debt vs income',
            value: readNumber(value) != null ? fmtPct(readNumber(value)!) : '—',
          },
          { label: 'Decision impact', value: 'None — note only' },
        ],
      };

    case 'LOAN_VELOCITY':
      return {
        code,
        category,
        categoryLabel,
        title: 'Recent loan activity',
        tone: 'warning',
        factors: [
          { label: 'Disbursements (90d)', value: String(readNumber(value) ?? '—') },
          { label: 'Decision impact', value: 'None — note only' },
        ],
      };

    case 'ELOD_UNAVAILABLE':
      return {
        code,
        category,
        categoryLabel,
        title: 'ELOD unavailable',
        tone: 'warning',
        factors: [
          { label: 'Existing debt used', value: '$0/mo (assumed)' },
          { label: 'Decision impact', value: 'None — note only' },
        ],
      };

    case 'CAPPED_TO_REQUESTED':
      return {
        code,
        category,
        categoryLabel,
        title: 'Capped to requested amount',
        tone: 'neutral',
        factors: [
          {
            label: 'Offer amount',
            value: readNumber(value) != null ? fmtMoney(readNumber(value)!) : '—',
          },
        ],
      };

    case 'LEGACY_PRA_GRID': {
      const table = message.match(/scoring table (\d+)/i)?.[1];
      const categoryN = message.match(/category (\d+)/i)?.[1];
      const limit = parseMoneyFromMessage(message, /limit \$?([\d,]+)/i);
      return {
        code,
        category,
        categoryLabel,
        title: 'Pre-approval table lookup',
        tone: 'neutral',
        factors: [
          ...(table ? [{ label: 'Scoring table', value: table }] : []),
          ...(categoryN ? [{ label: 'Customer tier', value: categoryN }] : []),
          ...(limit ? [{ label: 'Table limit', value: limit }] : []),
        ],
      };
    }

    case 'BINDING_LEGACY_PRA': {
      const legacy = parseMoneyFromMessage(message, /grid\s*\(\$?([\d,]+)\)/i);
      const bank = parseMoneyFromMessage(message, /affordability\s*\(\$?([\d,]+)\)/i);
      return {
        code,
        category,
        categoryLabel,
        title: 'Pre-approval table is the binding limit',
        tone: 'warning',
        factors: [
          ...(legacy ? [{ label: 'Table limit', value: legacy }] : []),
          ...(bank ? [{ label: 'Bank ceiling', value: bank }] : []),
          { label: 'Effect', value: 'Table limit wins — lower offer' },
        ],
      };
    }

    case 'BINDING_AFFORDABILITY': {
      const bank = parseMoneyFromMessage(message, /ELOD\s*\(\$?([\d,]+)\)/i);
      const legacy = parseMoneyFromMessage(message, /grid\s*\(\$?([\d,]+)\)/i);
      return {
        code,
        category,
        categoryLabel,
        title: 'Bank payment room is the binding limit',
        tone: 'warning',
        factors: [
          ...(bank ? [{ label: 'Bank ceiling', value: bank }] : []),
          ...(legacy ? [{ label: 'Table limit', value: legacy }] : []),
          { label: 'Effect', value: 'Payment room wins — lower offer' },
        ],
      };
    }

    case 'JURISDICTION_BLOCKED':
      return {
        code,
        category,
        categoryLabel,
        title: 'Jurisdiction not eligible',
        tone: 'error',
        factors: [
          { label: 'Reason', value: 'QC / territories — payday not offered' },
        ],
      };

    case 'FEDERAL_AMOUNT_EXCEEDED':
      return {
        code,
        category,
        categoryLabel,
        title: 'Amount over federal max',
        tone: 'error',
        factors: [
          {
            label: 'Requested',
            value: readNumber(value) != null ? fmtMoney(readNumber(value)!) : '—',
          },
          { label: 'Federal max', value: fmtMoney(1500) },
        ],
      };

    default:
      return {
        code,
        category,
        categoryLabel,
        title: code.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
        tone: 'neutral',
        factors: [{ label: 'Detail', value: message.length > 80 ? `${message.slice(0, 77)}…` : message }],
      };
  }
}

export function buildPdlRemarkCards(
  pdl?: PdlAffordabilityJourneyDto | null,
): PdlRemarkCard[] {
  const remarks = pdl?.remarks ?? [];
  return remarks.map((r) =>
    presentPdlRemark({
      code: r.code,
      category: r.category,
      message: r.message,
      value: r.value,
    }),
  );
}

export function groupPdlRemarkCards(cards: PdlRemarkCard[]): Array<{
  category: string;
  categoryLabel: string;
  cards: PdlRemarkCard[];
}> {
  const byCategory = new Map<string, PdlRemarkCard[]>();
  for (const card of cards) {
    const list = byCategory.get(card.category) ?? [];
    list.push(card);
    byCategory.set(card.category, list);
  }

  const ordered = [
    ...CATEGORY_ORDER.filter((c) => byCategory.has(c)),
    ...[...byCategory.keys()].filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  return ordered.map((category) => ({
    category,
    categoryLabel: CATEGORY_LABELS[category] ?? category,
    cards: byCategory.get(category) ?? [],
  }));
}

export function tonePalette(tone: PdlRemarkTone): {
  border: string;
  chip: 'default' | 'info' | 'success' | 'warning' | 'error';
} {
  switch (tone) {
    case 'success':
      return { border: 'success.main', chip: 'success' };
    case 'warning':
      return { border: 'warning.main', chip: 'warning' };
    case 'error':
      return { border: 'error.main', chip: 'error' };
    case 'info':
      return { border: 'info.main', chip: 'info' };
    default:
      return { border: 'divider', chip: 'default' };
  }
}

export function remarkCardBackground(tone: PdlRemarkTone, mode: 'light' | 'dark'): string {
  if (mode === 'dark') {
    switch (tone) {
      case 'success':
        return 'rgba(102, 187, 106, 0.12)';
      case 'warning':
        return 'rgba(255, 167, 38, 0.12)';
      case 'error':
        return 'rgba(239, 83, 80, 0.12)';
      case 'info':
        return 'rgba(41, 182, 246, 0.12)';
      default:
        return 'rgba(255, 255, 255, 0.04)';
    }
  }
  switch (tone) {
    case 'success':
      return 'rgba(46, 125, 50, 0.08)';
    case 'warning':
      return 'rgba(237, 108, 2, 0.08)';
    case 'error':
      return 'rgba(211, 47, 47, 0.08)';
    case 'info':
      return 'rgba(2, 136, 209, 0.08)';
    default:
      return 'rgba(0, 0, 0, 0.03)';
  }
}
